import React, { FC, useEffect, useState } from "react";
import { Flex } from "reflexbox";
import { FormattedMessage } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Drawer from "@components/Drawer";
import { Text } from "@components/Text";
import { OldInput } from "@components/OldInput";
import ExpandableRow from "@components/ExpandableRow";
import { ListContainer } from "@components/ListContainer";
import { Column } from "@components/ExpandableRow/styles";
import { ScrollableList } from "@components/ScrollableList";
import { Button, EButtonType } from "@components/OldButton";

import { useFormatMessage } from "@hooks/formatMessage";
import { ReactComponent as IconFilter } from "@assets/icons/filter.svg";

import "../../../styles/index.scss";
import { TDispatch, TRootState } from "../../../redux/types";
import { fetchEmailLogList, incrementEmailLogsPageNo, setEmailLogsPageNo } from "./ducks";

const EmailLogs: FC = () => {
  const [filtersOpened, setFiltersOpened] = useState(window.innerWidth >= 1400);
  const [emailLogListFilter, setEmailLogListFilter] = useState({
    GenericText: null,
    Username: null,
    FirstName: null,
    LastName: null,
    dynamicFilter: "",
  });

  const dispatch: TDispatch = useDispatch();
  const formatMessage = useFormatMessage();

  const { emailLogs, emailLogsPageNo, moreEmailLogs } = useSelector(
    ({ EmailLogsReducer: { emailLogs, emailLogsPageNo, moreEmailLogs } }: TRootState) => ({
      emailLogs,
      emailLogsPageNo,
      moreEmailLogs,
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(fetchEmailLogList()).then(() => dispatch(incrementEmailLogsPageNo()));
  }, [dispatch]);

  const handleScroll = () => {
    if (moreEmailLogs) {
      dispatch(fetchEmailLogList(emailLogsPageNo)).then(() => dispatch(incrementEmailLogsPageNo()));
    }
  };

  const changeFilterOpened = () => {
    setFiltersOpened(prev => !prev);
  };

  const filterEmailLogs = () => {
    setEmailLogsPageNo(1);
    fetchEmailLogList(1, 10, emailLogListFilter);
  };

  const clearFilters = () => {
    setEmailLogListFilter(prev => ({
      ...prev,
      dynamicFilter: "",
    }));

    dispatch(setEmailLogsPageNo(1));
    dispatch(fetchEmailLogList(1, 10));
  };

  const renderEmailLogsSideBar = () => {
    return (
      <Drawer id="filterArea" opened={filtersOpened}>
        <div className="paddingContainer">
          <p className="colorGrey mb-1">Dynamic Filter</p>
          <div data-cy="dynamicFilterByField">
            <OldInput
              id="dynamicSearch"
              name="dynamicSearch"
              key="dynamicSearch"
              inputWrapperClass="text-input filter-input"
              placeholder={formatMessage("app.anyfield", "Any Field")}
              value={emailLogListFilter.dynamicFilter}
              onChange={(name, value) =>
                setEmailLogListFilter(prev => ({
                  ...prev,
                  dynamicFilter: value,
                }))
              }
              validationMessageConfig={null /* this.validationMessageConfig() */}
              type="text"
              inputWrapperStyle={{}}
              filtering
            />
          </div>

          <div className="breaker mb-5" />

          <div className="modal-buttons">
            <Button
              dataCyName="buttonClearSearch"
              className="modal-sub-button primary"
              onClick={e => {
                e.stopPropagation();
                clearFilters();
              }}
              buttonType={EButtonType.ButtonText}
            >
              <FormattedMessage id="app.clear" defaultMessage="Clear" />
            </Button>
            <Button
              dataCyName="buttonSearch"
              className="modal-main-button primary"
              onClick={e => {
                e.stopPropagation();
                filterEmailLogs();
              }}
              buttonType={EButtonType.Button}
            >
              <FormattedMessage id="app.search" defaultMessage="Search" />
            </Button>
          </div>
        </div>
      </Drawer>
    );
  };

  return (
    <ListContainer>
      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <ScrollableList
          items={emailLogs}
          onLoadMore={handleScroll}
          renderHeader={
            <>
              <Column size={1}>
                <Text>{formatMessage("app.emailStatusName", "Status")}</Text>
              </Column>
              <Column size={1}>
                <Text>{formatMessage("app.timestamp", "Date")}</Text>
              </Column>
              <Column size={1}>
                <Text>{formatMessage("app.recipient", "Recipient")}</Text>
              </Column>
              <Column size={1}>
                <Text>{formatMessage("app.subject", "Subject")}</Text>
              </Column>
              <Column size={1} icon>
                <div
                  id="toggleFilterArea"
                  className="icon-filter"
                  tabIndex={0}
                  onClick={e => {
                    e.stopPropagation();

                    changeFilterOpened();
                  }}
                >
                  <IconFilter />
                </div>
              </Column>
            </>
          }
          renderItem={(item, index) => (
            <ExpandableRow
              data-cy="emailRow"
              key={"userRow" + index}
              id={`emailLogRow ${index}`}
              index={index}
              translucent={false}
              expandable={true}
              top={
                <>
                  <Column size={1}>
                    <Text light={false} fontSize="16px" data-cy="emailStatusName">
                      {item.emailStatusName}
                    </Text>
                  </Column>
                  <Column size={2}>
                    <Text light={false} fontSize="16px" data-cy="timestamp">
                      {item.timestamp}
                    </Text>
                  </Column>
                  <Column size={3}>
                    <Text light={false} fontSize="16px" data-cy="recipient">
                      {item.recipient}
                    </Text>
                  </Column>
                  <Column size={3}>
                    <Text light={false} fontSize="16px" data-cy="subject">
                      {item.subject}
                    </Text>
                  </Column>
                </>
              }
              more={
                <>
                  <Flex width="100%" mb="20px">
                    <Flex flexDirection="column" flex={1} mr="20px">
                      <Text mb="20px">{formatMessage("app.message", "Message")}</Text>
                      <Text light={false} fontSize="16px">
                        {item.message}
                      </Text>
                    </Flex>
                    <Flex flexDirection="column" flex={1}>
                      <Text mb="20px">{formatMessage("app.text", "Text")}</Text>
                      <Text light={false} fontSize="16px">
                        {item.text}
                      </Text>
                    </Flex>
                  </Flex>
                </>
              }
            />
          )}
        />
        {renderEmailLogsSideBar()}
      </Flex>
    </ListContainer>
  );
};

export default EmailLogs;
